/* eslint-disable @typescript-eslint/camelcase */
// (the Elasticsearch query DSL and the App-Search-era field names are snake_case)
import _ from 'lodash';

import { __DEV__ } from '../../constants';

/**
 * Self-hosted Elasticsearch connector for @elastic/search-ui (TAS-1852).
 *
 * Drop-in replacement for the old AppSearchAPIConnector: it implements the
 * search-ui connector interface (`onSearch`) and translates the search-ui
 * request state + our App-Search-style `searchQuery` config (`search_fields`,
 * `result_fields`, filters) into the canonical Elasticsearch queries from
 * `infra/elasticsearch/QUERY-DESIGN.md`. The ES response is adapted back into
 * the App-Search-like `{ field: { raw, snippet } }` result shape, which
 * `parseResult` below unwraps for the components.
 *
 * Engine names map 1:1 onto Elasticsearch index names.
 */

// Read-only, index-scoped search key (client-embeddable, like the old
// App Search public search key).
const ELASTICSEARCH_PUBLIC_SEARCH_KEY = __DEV__
  ? 'MVN6SEo1OEJobDVnVGpIQVRyMjA6RFNHR0dwcTM3VV9OZHZVQWRQSHpoQQ=='
  : 'U3NaektwOEItNWtLdGxEOUJwWjc6S3pJdGNNWUJfVTFlOV9LV09VenZIUQ=='; // TODO(TAS-1852): set when prod ES is provisioned
const ELASTICSEARCH_API_BASE_ENDPOINT = __DEV__
  ? 'https://search-dev.redkiwiapp.com'
  : 'https://search.redkiwiapp.com';

// Translation-map ("object"-mapped) fields. App Search indexed these as one
// flattened JSON string; in ES every language value is its own analyzed
// subfield (dynamic `rk_text` template), so they are searched via `field.*`.
const OBJECT_SEARCH_FIELDS = [
  'subtitle_translations',
  'subtitle_translations_by_gengo',
  'tag_translations',
  'text_translations',
  'text_translations_by_gengo',
  'original_text_translations',
  'language_code_to_name',
  'language_code_to_description',
];

// Fields mapped as top-level `keyword` in infra/elasticsearch/mappings (no
// `.keyword` subfield). Every other string field we filter on is analyzed
// `text` with a `.keyword` subfield.
const KEYWORD_MAPPED_FIELDS = [
  'id',
  'lemma_ids',
  'tag_keys',
  'topic_ids',
  'grammar_ids',
  'learning_language_codes',
  'language_code',
  'learning_language_code',
  'category',
  'fragment_key',
  'difficulty_level_string',
  'image_path',
  'type',
];

interface SearchUiFilter {
  field: string;
  values: unknown[];
  type?: string;
}

interface RequestState {
  searchTerm?: string;
  current?: number;
  resultsPerPage?: number;
  filters?: SearchUiFilter[];
  sortField?: string;
  sortDirection?: string;
}

interface QueryConfig {
  current?: number;
  resultsPerPage?: number;
  filters?: SearchUiFilter[];
  sortField?: string;
  sortDirection?: string;
  precision?: number;
  search_fields?: { [field: string]: { weight?: number } };
  result_fields?: {
    [field: string]: { raw?: unknown; snippet?: unknown };
  };
}

/**
 * Translates one App Search value/range filter leaf (`{ field: value }`) to
 * an ES filter clause. App Search value filters are exact, case-sensitive
 * matches on the whole field value; for analyzed text fields that is the
 * `.keyword` subfield in our mappings. Range filters: `from` is inclusive,
 * `to` is exclusive (App Search semantics).
 */
const translateValueFilter = (field: string, value: any): any => {
  if (
    _.isPlainObject(value) &&
    (value.from !== undefined || value.to !== undefined)
  ) {
    return {
      range: {
        [field]: {
          ...(value.from !== undefined && { gte: value.from }),
          ...(value.to !== undefined && { lt: value.to }),
        },
      },
    };
  }
  const values = Array.isArray(value) ? value : [value];
  const useKeywordSubfield =
    !KEYWORD_MAPPED_FIELDS.includes(field) &&
    values.every((v) => typeof v === 'string');
  const targetField = useKeywordSubfield ? `${field}.keyword` : field;
  return { terms: { [targetField]: values } };
};

/**
 * Translates an App Search filter node (the `{ all / any / none }` grammar,
 * possibly nested — e.g. the root-`any` trick the lemma inputs use via
 * `addFilter('any', { lemma: q }, 'any')`) into an ES bool query. Filters run
 * in filter context (no scoring), matching App Search semantics.
 */
const translateAppSearchFilter = (filter: { [key: string]: any }): any => {
  const clauses = Object.entries(filter).map(([key, value]) => {
    if (key === 'all' || key === 'any' || key === 'none') {
      const subFilters = (Array.isArray(value) ? value : [value]).map(
        translateAppSearchFilter,
      );
      if (key === 'all') {
        return { bool: { filter: subFilters } };
      }
      if (key === 'any') {
        return { bool: { should: subFilters, minimum_should_match: 1 } };
      }
      return { bool: { must_not: subFilters } };
    }
    return translateValueFilter(key, value);
  });
  return clauses.length === 1 ? clauses[0] : { bool: { filter: clauses } };
};

/**
 * Rebuilds the App Search filter object from search-ui filter state, exactly
 * like the old AppSearchAPIConnector request adapter did:
 * `{ all: [ { <type>: [ { <field>: <value> }, ... ] }, ... ] }`.
 */
const searchUiFiltersToAppSearchFilter = (
  filters?: SearchUiFilter[],
): { [key: string]: any } | undefined => {
  if (filters == null || filters.length === 0) {
    return undefined;
  }
  return {
    all: filters.map((f) => ({
      [f.type || 'any']: (f.values || []).map((v) => ({ [f.field]: v })),
    })),
  };
};

/**
 * Converts a `search_fields` config into an ES `multi_match` field list with
 * boosts. `.std` are standard-analyzer subfields for stopword-only queries
 * (see QUERY-DESIGN.md); ES silently skips unmapped fields, so including them
 * is safe on indices that do not have them (yet).
 */
const buildSearchFields = (searchFields?: {
  [field: string]: { weight?: number };
}): string[] =>
  _.flatMap(Object.entries(searchFields ?? {}), ([field, options]) => {
    const weight = options?.weight ?? 1;
    if (OBJECT_SEARCH_FIELDS.includes(field)) {
      return [`${field}.*^${weight}`];
    }
    return [`${field}^${weight}`, `${field}.std^${weight / 2}`];
  });

/**
 * Full-text query. Every TMS search config sends App Search `precision: 8`,
 * which (verified against the dev App Search engine) requires every query
 * term to match — across fields — with no typo tolerance. `cross_fields` +
 * `operator: and` reproduces that. Empty query = match everything (App
 * Search returned all documents for an empty query), which the filter-only
 * flows (lemma / picker inputs) rely on.
 */
const buildEsQuery = (
  searchTerm: string,
  fields: string[],
  esFilter?: any,
): any => {
  const must = _.isEmpty(searchTerm)
    ? { match_all: {} }
    : {
        multi_match: {
          query: searchTerm,
          type: 'cross_fields',
          operator: 'and',
          lenient: true,
          fields,
        },
      };
  return {
    bool: {
      ...(esFilter != null && { filter: [esFilter] }),
      must: [must],
    },
  };
};

/**
 * Highlight config for the `result_fields` that request a snippet. The
 * `html` encoder matches App Search's escaped snippets (components render
 * them via dangerouslySetInnerHTML). `require_field_match: false` because
 * the match may have happened on a sibling subfield (`.std`, translations).
 */
const buildHighlight = (
  searchTerm: string,
  resultFields: QueryConfig['result_fields'],
): any => {
  const snippetFields = Object.keys(resultFields ?? {}).filter(
    (field) => resultFields?.[field]?.snippet != null,
  );
  if (_.isEmpty(searchTerm) || snippetFields.length === 0) {
    return undefined;
  }
  return {
    encoder: 'html',
    require_field_match: false,
    fields: snippetFields.reduce(
      (acc, field) => ({ ...acc, [field]: { number_of_fragments: 0 } }),
      {},
    ),
  };
};

class ElasticsearchAPIConnector {
  private engineName: string;

  constructor(engineName: string) {
    this.engineName = engineName;
  }

  // Click-through analytics are an App Search feature; no-op on ES.
  onResultClick(): void {}

  onAutocompleteResultClick(): void {}

  // TMS does not use search-ui autocomplete.
  async onAutocomplete(): Promise<{ [key: string]: unknown }> {
    return {};
  }

  async onSearch(
    state: RequestState,
    queryConfig: QueryConfig,
  ): Promise<{ [key: string]: unknown }> {
    // Like the old AppSearchAPIConnector, queryConfig values override state.
    const current = queryConfig.current ?? state.current ?? 1;
    const resultsPerPage = queryConfig.resultsPerPage ?? state.resultsPerPage;
    const filters = queryConfig.filters ?? state.filters;
    const sortField = queryConfig.sortField ?? state.sortField;
    const sortDirection = queryConfig.sortDirection ?? state.sortDirection;
    const searchTerm = state.searchTerm ?? '';
    const resultFields = queryConfig.result_fields ?? {};

    const size = resultsPerPage ?? 20;
    const appSearchFilter = searchUiFiltersToAppSearchFilter(filters);
    const esFilter =
      appSearchFilter != null
        ? translateAppSearchFilter(appSearchFilter)
        : undefined;
    const highlight = buildHighlight(searchTerm, resultFields);
    const sort =
      !_.isEmpty(sortField) && !_.isEmpty(sortDirection)
        ? [{ [sortField as string]: sortDirection }]
        : undefined;

    const requestBody = {
      size,
      from: (current - 1) * size,
      _source: _.uniq([...Object.keys(resultFields), 'id']),
      query: buildEsQuery(
        searchTerm,
        buildSearchFields(queryConfig.search_fields),
        esFilter,
      ),
      ...(sort != null && { sort }),
      ...(highlight != null && { highlight }),
    };

    const response = await fetch(
      `${ELASTICSEARCH_API_BASE_ENDPOINT}/${this.engineName}/_search`,
      {
        method: 'POST',
        headers: {
          Authorization: `ApiKey ${ELASTICSEARCH_PUBLIC_SEARCH_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      },
    );
    if (!response.ok) {
      throw new Error(
        `Elasticsearch search failed: ${
          response.status
        } ${await response.text()}`,
      );
    }
    const responseBody = await response.json();
    const hits: any[] = responseBody?.hits?.hits ?? [];
    const totalResults: number = responseBody?.hits?.total?.value ?? 0;
    const totalPages =
      size > 0 ? Math.min(Math.ceil(totalResults / size), 100) : 0;

    const results = hits.map((hit) => {
      const source = hit._source ?? {};
      const highlights = hit.highlight ?? {};
      const wrapped: { [key: string]: unknown } = {};
      Object.keys(resultFields).forEach((field) => {
        wrapped[field] = { raw: source[field], snippet: highlights[field] };
      });
      wrapped.id = { raw: source.id ?? hit._id };
      wrapped._meta = {
        id: hit._id,
        engine: this.engineName,
        score: hit._score,
      };
      return wrapped;
    });

    return {
      results,
      totalResults,
      totalPages,
      requestId: '',
      rawResponse: responseBody,
    };
  }
}

export const createConnector = (engineName: string): unknown =>
  new ElasticsearchAPIConnector(engineName);

const stripHighlightTags = (value: string): string =>
  // The ES `html` highlight encoder also escapes `'` and `/` (as hex
  // entities), which lodash's unescape does not cover.
  _.unescape(value.replace(/<\/?em>/g, ''))
    .replace(/&#x27;/g, "'")
    .replace(/&#x2F;/g, '/');

/**
 * Unwraps a `{ field: { raw, snippet } }` result into plain field values,
 * preferring highlighted snippets where present (like the old App Search
 * parseResult). ES returns real objects/arrays, so no JSON re-parsing of
 * App Search's stringified maps is needed anymore.
 */
export const parseResult = (result: any): any =>
  _.mapValues(result, (o) => {
    if (o == null || typeof o !== 'object' || !('raw' in o)) {
      return o; // e.g. `_meta`
    }
    const { raw, snippet }: { raw?: any; snippet?: string[] } = o;
    if (snippet == null || snippet.length === 0) {
      return raw;
    }
    if (Array.isArray(raw)) {
      // ES returns one highlighted fragment per matched array element;
      // splice them back into the raw array (as App Search snippets did).
      const highlightedByPlainText: { [plain: string]: string } = {};
      snippet.forEach((s) => {
        highlightedByPlainText[stripHighlightTags(s)] = s;
      });
      return raw.map((v) =>
        typeof v === 'string' && highlightedByPlainText[v] != null
          ? highlightedByPlainText[v]
          : v,
      );
    }
    if (typeof raw === 'string') {
      return snippet[0];
    }
    return raw;
  });
